import { createContext, useContext, useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { api } from '../services/api';
import { telemetrySignOut } from '../services/telemetry';
import { loginRequest } from '../msalConfig';

const AuthContext = createContext(null);

// The token exchange is the one API call that cannot go through services/api.js:
// a bad token comes back 401, and that interceptor treats any 401 as a dead
// session — it clears storage and hard-redirects to /login, which would bury
// the real error. So it uses fetch directly, and therefore has to resolve the
// API base itself, exactly as api.js does.
//
// It must be the SAME base, not a relative '/api' path. Under docker compose
// the browser is told to call the API's published port directly
// (VITE_API_BASE_URL=http://localhost:3002) because vite's proxy targets
// localhost:3002, which inside the frontend container is the frontend
// container, not the api service. A relative URL here goes to that dead proxy
// and the exchange fails after Microsoft has already signed the user in — the
// login appears to break with the account perfectly valid.
// Empty when unset, so `npm run dev` on the host still goes through the proxy.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [user,              setUser]              = useState(null);
  const [costCenters,       setCostCenters]       = useState([]);
  const [isAdmin,           setIsAdmin]           = useState(false);
  const [isFinanceReviewer, setIsFinanceReviewer] = useState(false);
  const [hasOvertimeAccess, setHasOvertimeAccess] = useState(false);
  // Server-owned policy, not a per-user grant. Defaults false so a failed or
  // in-flight /me never briefly renders a nav link the server would refuse —
  // the server is the enforcement either way, but a link that appears and then
  // vanishes reads as a bug.
  const [inventoryViewIsUniversal, setInventoryViewIsUniversal] = useState(false);
  const [loading,           setLoading]           = useState(true);
  const [loadTick,          setLoadTick]          = useState(0);

  useEffect(() => {
    // Block until MSAL has finished processing the redirect (or any other
    // in-progress interaction). While inProgress !== None, loading stays true
    // so the app shows the spinner rather than a blank shell.
    if (inProgress !== InteractionStatus.None) return;

    // Cancellation flag so stale async completions (from React StrictMode's
    // double-invocation, or from a dep change mid-flight) don't write state.
    let cancelled = false;

    async function loadUser() {
      setLoading(true);
      try {
        // ── Path 1: app-issued JWT already in storage ─────────────
        // Covers both local-admin sessions and returning MSAL sessions
        // where a token was stored on a previous load.
        const localToken = localStorage.getItem('mor_token');
        if (localToken) {
          const data = await api.get('/auth/me');
          if (!cancelled) {
            setUser(data.user);
            setCostCenters(data.costCenters);
            setIsAdmin(data.isAdmin);
            setIsFinanceReviewer(!!data.isFinanceReviewer);
            setHasOvertimeAccess(!!data.hasOvertimeAccess);
            setInventoryViewIsUniversal(!!data.inventoryViewIsUniversal);
          }
          return;
        }

        // ── Path 2: MSAL redirect just completed ──────────────────
        // inProgress is now None and isAuthenticated is true, but no
        // local JWT exists yet. Acquire the ID token from MSAL's cache
        // and exchange it for a backend JWT via POST /api/auth/azure.
        const account = instance.getActiveAccount() || accounts[0];
        if (isAuthenticated && account) {
          const tokenResult = await instance.acquireTokenSilent({
            scopes:  loginRequest.scopes,
            account,
          });

          const exchangeRes = await fetch(`${API_BASE}/api/auth/azure`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ idToken: tokenResult.idToken }),
          });

          if (!exchangeRes.ok) {
            const body = await exchangeRes.json().catch(() => ({}));
            throw new Error(body.error || 'Token exchange failed');
          }

          const exchanged = await exchangeRes.json();
          // Set localStorage unconditionally so the /auth/me call below
          // has the Authorization header available via the axios interceptor.
          localStorage.setItem('mor_token', exchanged.token);
          localStorage.setItem('mor_user',  JSON.stringify(exchanged.user));

          const meData = await api.get('/auth/me');
          if (!cancelled) {
            setUser(meData.user);
            setCostCenters(meData.costCenters);
            setIsAdmin(meData.isAdmin);
            setIsFinanceReviewer(!!meData.isFinanceReviewer);
            setHasOvertimeAccess(!!meData.hasOvertimeAccess);
            setInventoryViewIsUniversal(!!meData.inventoryViewIsUniversal);
          }
          return;
        }

        // ── Path 3: not authenticated ─────────────────────────────
        // Falls through to finally → setLoading(false)
      } catch {
        if (!cancelled) {
          localStorage.removeItem('mor_token');
          localStorage.removeItem('mor_user');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadUser();
    return () => { cancelled = true; };
  }, [inProgress, isAuthenticated, loadTick]);

  // Called by local login after storing the token. loadTick as a dep
  // (not a ref guard) means the effect re-runs even though no MSAL state
  // changed. setLoading(true) here is in the same React batch as the
  // caller's navigate() so AppRoutes shows the spinner, not a blank shell.
  function reloadAuth() {
    setLoading(true);
    setLoadTick((t) => t + 1);
  }

  // Merges a change the server has just confirmed (the start page, today) into
  // the signed-in user, so it takes effect without reloading /auth/me -- which
  // would flash the full-screen spinner for a one-field change.
  function updateUser(patch) {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }

  // Recorded before the token is dropped, because both calls need it: the
  // audit log's LOGOUT (without it a sign-out leaves no trace) and the end of
  // the visit in usage analytics. Each is capped at 1.5s and may fail — a
  // sign-out never waits on, or fails for, bookkeeping.
  async function logout() {
    const capped = (p) => Promise.race([Promise.resolve(p).catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
    await Promise.all([capped(telemetrySignOut()), capped(api.post('/auth/logout'))]);
    localStorage.removeItem('mor_token');
    localStorage.removeItem('mor_user');
    const account = accounts[0];
    if (account) {
      instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    } else {
      window.location.href = '/login';
    }
  }

  return (
    <AuthContext.Provider value={{
      user, costCenters, isAdmin, isFinanceReviewer, hasOvertimeAccess,
      inventoryViewIsUniversal, loading, logout, reloadAuth, updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
